/**
 * headlight-entity-navigation — where a record opens, for whom.
 *
 * Rules are data: an ordered map per entity type (stored in the `EntityDestinations` configuration
 * namespace, NamespaceValue = entity name, so Global → Customer → Project cascade merges them by
 * rule key). A rule matches when its Audience matches the signed-in user and its When conditions
 * match the record; the first match is the primary destination and the rest are alternatives.
 *
 * DOM-free and dependency-light so the links server and browser apps resolve identically.
 */

const libHosts = require('headlight-qrcode/source/Headlight-QRCode-Hosts.js');

/**
 * Apps a destination can target. Module gates only where an app really checks one (LIMS does not).
 *
 * @type {Record<string, { Subdomain: string, Module?: string }>}
 */
const APPS =
{
	LIMS: { Subdomain: 'lims' },
	Fieldbook: { Subdomain: 'fieldbook', Module: 'HEADLIGHT_FIELDBOOK' },
	Vault: { Subdomain: 'vault', Module: 'HEADLIGHT_VAULT' },
	Portal: { Subdomain: 'portal' },
	Home: { Subdomain: 'home', Module: 'HeadLight' },
	Config: { Subdomain: 'config' },
	Links: { Subdomain: 'links' },
};

/**
 * @typedef {Object} DestinationRule
 * @property {number} [Order] - Lower runs first. Default 100.
 * @property {boolean} [Enabled] - false turns a rule off (e.g. a customer disabling a platform rule).
 * @property {string} Label - Plain-language button text, e.g. "Open in LIMS".
 * @property {{ Roles?: Array<string>, PermissionLevels?: Array<string>, MinimumRoleIndex?: number, Modules?: Array<string>, From?: Array<string> }} [Audience]
 * @property {Record<string, Array<any>>} [When] - e.g. `{ "Record.Status": [ "RECV" ], "Project.Status": [ "Closed" ] }`
 * @property {{ App?: string, Route?: string, Via?: string, Pick?: 'Oldest'|'Newest', Landing?: boolean }} Target
 */

/**
 * @typedef {Object} Destination
 * @property {string} Key - The rule key.
 * @property {string} Label
 * @property {'URL'|'Via'|'Landing'} Kind
 * @property {string} [URL]
 * @property {string} [App]
 * @property {string} [Via] - Related entity to resolve instead (e.g. Document).
 * @property {'Oldest'|'Newest'} [Pick]
 */

/**
 * @param {any} pObject
 * @param {string} pAddress - Dotted path.
 * @return {any}
 */
function getAtAddress(pObject, pAddress)
{
	let tmpValue = pObject;
	for (const tmpPart of String(pAddress).split('.'))
	{
		if (tmpValue === null || tmpValue === undefined)
		{
			return undefined;
		}
		tmpValue = tmpValue[tmpPart];
	}
	return tmpValue;
}

/**
 * Fill `{~D:Record.GUIDSample~}` style tokens, URI-encoding each value. Nothing else is evaluated:
 * route templates are written by administrators, so they get data substitution only.
 *
 * @param {string} pTemplate
 * @param {Object} pData - `{ Record, Project, Document, Session }`
 * @return {string|null} null when a referenced value is missing
 */
function fillRouteTemplate(pTemplate, pData)
{
	let tmpMissing = false;
	const tmpResult = String(pTemplate || '').replace(/\{~D:([A-Za-z0-9_.]+)~\}/g, (pMatch, pAddress) =>
	{
		const tmpValue = getAtAddress(pData, pAddress);
		if (tmpValue === undefined || tmpValue === null || tmpValue === '')
		{
			tmpMissing = true;
			return '';
		}
		return encodeURIComponent(String(tmpValue));
	});
	return tmpMissing ? null : tmpResult;
}

/**
 * @param {DestinationRule['Audience']} pAudience
 * @param {Object} pSession - `{ UserRoleIndex, UserRoles: [{ Hash, Name, PermissionLevel }] }`
 * @param {Array<string>|null} pModules
 * @param {string} [pFrom]
 * @return {{ Match: boolean, Reason?: string }}
 */
function matchAudience(pAudience, pSession, pModules, pFrom)
{
	if (!pAudience)
	{
		return { Match: true };
	}
	const tmpSession = pSession || {};
	const tmpRoles = Array.isArray(tmpSession.UserRoles) ? tmpSession.UserRoles : [];
	if (Array.isArray(pAudience.Roles) && pAudience.Roles.length > 0)
	{
		const tmpHasRole = tmpRoles.some((pRole) => pAudience.Roles.includes(pRole.Hash) || pAudience.Roles.includes(pRole.Name));
		if (!tmpHasRole)
		{
			return { Match: false, Reason: 'role' };
		}
	}
	if (Array.isArray(pAudience.PermissionLevels) && pAudience.PermissionLevels.length > 0)
	{
		if (!tmpRoles.some((pRole) => pAudience.PermissionLevels.includes(pRole.PermissionLevel)))
		{
			return { Match: false, Reason: 'permission level' };
		}
	}
	if (typeof pAudience.MinimumRoleIndex === 'number' && !(Number(tmpSession.UserRoleIndex) >= pAudience.MinimumRoleIndex))
	{
		return { Match: false, Reason: 'role level' };
	}
	if (Array.isArray(pAudience.Modules) && pAudience.Modules.length > 0)
	{
		if (!pModules || !pAudience.Modules.some((pModule) => pModules.includes(pModule)))
		{
			return { Match: false, Reason: 'module' };
		}
	}
	if (Array.isArray(pAudience.From) && pAudience.From.length > 0 && !pAudience.From.includes(pFrom))
	{
		return { Match: false, Reason: 'calling app' };
	}
	return { Match: true };
}

/**
 * @param {Record<string, Array<any>>} pWhen
 * @param {Object} pData
 * @return {{ Match: boolean, Reason?: string }}
 */
function matchWhen(pWhen, pData)
{
	if (!pWhen)
	{
		return { Match: true };
	}
	for (const [ tmpAddress, tmpAllowed ] of Object.entries(pWhen))
	{
		const tmpValue = getAtAddress(pData, tmpAddress);
		const tmpList = Array.isArray(tmpAllowed) ? tmpAllowed : [ tmpAllowed ];
		if (!tmpList.some((pAllowed) => String(pAllowed) === String(tmpValue)))
		{
			return { Match: false, Reason: `${tmpAddress} is ${tmpValue === undefined ? 'unset' : JSON.stringify(tmpValue)}` };
		}
	}
	return { Match: true };
}

/**
 * @param {Record<string, DestinationRule>} pRules
 * @return {Array<[string, DestinationRule]>} enabled rules, by Order then key
 */
function orderRules(pRules)
{
	return Object.entries(pRules || {})
		.filter(([ , pRule ]) => pRule && typeof pRule === 'object' && pRule.Enabled !== false && pRule.Target)
		.sort(([ pKeyA, pA ], [ pKeyB, pB ]) => ((pA.Order ?? 100) - (pB.Order ?? 100)) || pKeyA.localeCompare(pKeyB));
}

/**
 * Resolve where a record opens.
 *
 * @param {Object} pRequest
 * @param {Record<string, DestinationRule>} pRequest.Rules - The merged rule map for this entity.
 * @param {Object} pRequest.Data - `{ Record, Project?, Document?, Session }` (addresses rules can test and template).
 * @param {Array<string>|null} [pRequest.Modules] - The user's modules, when any rule needs them.
 * @param {string} [pRequest.From] - The calling app (e.g. 'links', 'LIMS').
 * @param {string} [pRequest.Environment] - Environment name for app URLs. Default Production.
 * @return {{ Primary: Destination|null, Alternatives: Array<Destination>, Explain: Array<string> }}
 */
function resolveDestinations(pRequest)
{
	const tmpExplain = [];
	const tmpMatches = [];
	const tmpSeen = new Set();
	for (const [ tmpKey, tmpRule ] of orderRules(pRequest.Rules))
	{
		const tmpAudience = matchAudience(tmpRule.Audience, pRequest.Data && pRequest.Data.Session, pRequest.Modules || null, pRequest.From);
		if (!tmpAudience.Match)
		{
			tmpExplain.push(`${tmpKey}: skipped (${tmpAudience.Reason})`);
			continue;
		}
		const tmpWhen = matchWhen(tmpRule.When, pRequest.Data);
		if (!tmpWhen.Match)
		{
			tmpExplain.push(`${tmpKey}: skipped (${tmpWhen.Reason})`);
			continue;
		}
		const tmpTarget = tmpRule.Target;
		/** @type {Destination|null} */
		let tmpDestination = null;
		if (tmpTarget.Landing)
		{
			tmpDestination = { Key: tmpKey, Label: tmpRule.Label, Kind: 'Landing' };
		}
		else if (tmpTarget.Via)
		{
			tmpDestination = { Key: tmpKey, Label: tmpRule.Label, Kind: 'Via', Via: tmpTarget.Via, Pick: tmpTarget.Pick || 'Oldest' };
		}
		else if (tmpTarget.App && APPS[tmpTarget.App])
		{
			const tmpApp = APPS[tmpTarget.App];
			if (tmpApp.Module && pRequest.Modules && !pRequest.Modules.includes(tmpApp.Module))
			{
				tmpExplain.push(`${tmpKey}: skipped (no ${tmpTarget.App} module)`);
				continue;
			}
			const tmpRoute = fillRouteTemplate(tmpTarget.Route || '', pRequest.Data);
			if (tmpRoute === null)
			{
				tmpExplain.push(`${tmpKey}: skipped (route needs a value the record doesn't have)`);
				continue;
			}
			tmpDestination = { Key: tmpKey, Label: tmpRule.Label, Kind: 'URL', App: tmpTarget.App, URL: `${libHosts.getAppBaseURL(tmpApp.Subdomain, pRequest.Environment)}${tmpRoute}` };
		}
		else
		{
			tmpExplain.push(`${tmpKey}: skipped (unknown target)`);
			continue;
		}
		const tmpIdentity = tmpDestination.URL || `${tmpDestination.Kind}:${tmpDestination.Via || ''}`;
		if (tmpSeen.has(tmpIdentity))
		{
			continue;
		}
		tmpSeen.add(tmpIdentity);
		tmpExplain.push(`${tmpKey}: matched`);
		tmpMatches.push(tmpDestination);
	}
	return { Primary: tmpMatches[0] || null, Alternatives: tmpMatches.slice(1), Explain: tmpExplain };
}

/**
 * @param {Record<string, DestinationRule>} pRules
 * @return {boolean} true when at least one enabled rule exists
 */
function hasRules(pRules)
{
	return orderRules(pRules).length > 0;
}

/**
 * True when any enabled rule needs the user's module list (so callers only fetch it when needed).
 *
 * @param {Record<string, DestinationRule>} pRules
 * @return {boolean}
 */
function needsModules(pRules)
{
	return orderRules(pRules).some(([ , pRule ]) => (pRule.Audience && Array.isArray(pRule.Audience.Modules) && pRule.Audience.Modules.length > 0) || (pRule.Target.App && APPS[pRule.Target.App] && APPS[pRule.Target.App].Module));
}

module.exports =
{
	APPS,
	resolveDestinations,
	fillRouteTemplate,
	matchAudience,
	matchWhen,
	hasRules,
	needsModules,
};
